import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faList, faTrophy, faChartLine, faCompass, faPlus, faTags } from '@fortawesome/free-solid-svg-icons';
import { getExploreStats, updateSongVisibility } from '../../services/exploreApi';

const ExploreOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taskId, setTaskId] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getExploreStats();
      setStats(res.data?.data || {});
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch stats');
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handlePublicSong = async (e) => {
    e.preventDefault();
    if (!taskId.trim()) return;
    setUpdateLoading(true);
    setUpdateMessage(null);
    try {
      await updateSongVisibility({ task_id: taskId.trim(), is_public: isPublic });
      setUpdateMessage({ type: 'success', text: 'Song visibility updated' });
      setTaskId('');
      fetchStats();
    } catch (err) {
      setUpdateMessage({ type: 'danger', text: err.response?.data?.message || err.message || 'Failed to update' });
    } finally {
      setUpdateLoading(false);
    }
  };

  const statCards = [
    { key: 'public_songs_count', label: 'Public Songs', icon: faMusic, color: 'primary', link: '/explore/public-songs' },
    { key: 'total_songs_count', label: 'Total Songs', icon: faMusic, color: 'secondary' },
    { key: 'playlists_count', label: 'Active Playlists', icon: faList, color: 'info', link: '/explore/playlists' },
    { key: 'play_count_7d', label: 'Plays (7d)', icon: faChartLine, color: 'success' },
    { key: 'play_count_30d', label: 'Plays (30d)', icon: faTrophy, color: 'warning', link: '/explore/top-charts' }
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <FontAwesomeIcon icon={faCompass} className="me-2" />
          Explore Overview
        </h2>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Row className="mb-4">
            {statCards.map(({ key, label, icon, color, link }) => (
              <Col key={key} md={6} lg={4} className="mb-3">
                <Card className="h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="text-muted mb-1 small">{label}</p>
                        <h3 className="mb-0">{stats?.[key] ?? 0}</h3>
                        {link && (
                          <Link to={link} className="small">View →</Link>
                        )}
                      </div>
                      <FontAwesomeIcon icon={icon} className={`text-${color}`} size="2x" />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
            <Col md={6} lg={4} className="mb-3">
              <Card className="h-100">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1 small">Songs with tags</p>
                      <p className="mb-0 small">Moods: <strong>{stats?.songs_with_mood_count ?? 0}</strong></p>
                      <p className="mb-0 small">Genres: <strong>{stats?.songs_with_genre_count ?? 0}</strong></p>
                      <Link to="/explore/moods-genres" className="small">View →</Link>
                    </div>
                    <FontAwesomeIcon icon={faTags} className="text-info" size="2x" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card>
            <Card.Header>
              <strong>Quick: Public a song</strong>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handlePublicSong} className="row g-3 align-items-end">
                <Col md={4}>
                  <Form.Label>Task ID</Form.Label>
                  <Form.Control
                    type="text"
                    className="form-control"
                    placeholder="abc-xyz"
                    value={taskId}
                    onChange={(e) => setTaskId(e.target.value)}
                    disabled={updateLoading}
                  />
                </Col>
                <Col md={2}>
                  <Form.Check
                    type="checkbox"
                    id="isPublic"
                    label="Public"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                </Col>
                <Col md={2}>
                  <Button type="submit" variant="primary" disabled={updateLoading || !taskId.trim()}>
                    {updateLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faPlus} className="me-1" />Update</>}
                  </Button>
                </Col>
                {updateMessage && (
                  <Col md={12}>
                    <Alert variant={updateMessage.type} className="mb-0 py-2">{updateMessage.text}</Alert>
                  </Col>
                )}
              </Form>
            </Card.Body>
          </Card>

          <div className="mt-3">
            <Link to="/explore/public-songs" className="btn btn-outline-primary me-2">Public Songs</Link>
            <Link to="/explore/playlists" className="btn btn-outline-primary me-2">Playlists</Link>
            <Link to="/explore/top-charts" className="btn btn-outline-primary">Top Charts</Link>
          </div>
        </>
      )}
    </div>
  );
};

export default ExploreOverview;
